import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import UploadFile from "@mui/icons-material/UploadFile";
import { Chip, IconButton, Tooltip } from "@mui/material";
import { SparkleIcon } from "components/ArtificialIntelligence/SparkleIcon";
import { ReviewDocumentMetadataDialog } from "components/Document/ReviewDocumentMetadataDialog/ReviewDocumentMetadataDialog";
import { ProcessedManifestsQuery } from "generated-graphql/graphql";
import { useDocumentAI } from "hooks/useDocumentAI";
import { useQueueManifestDocumentExtraction } from "hooks/useQueueManifestDocumentExtraction";
import { useState } from "react";

import { ManifestUploadDialog } from "./ManifestUploadDialog";

type Manifest = ProcessedManifestsQuery["processedManifests"]["items"][number];

type ManifestDocumentActionsProps = {
  manifest: Manifest;
  tenantId: string;
};

/**
 * Renders document-related actions for a manifest:
 * - Upload button when no source document exists
 * - Review extraction button when source document has extracted data
 * - Review button (opens to document tab) when source document exists but no extracted data
 * - Needs-review chip when document metadata is flagged
 *
 * Respects staff + feature flag permissions via `canQueueManifestExtraction`.
 */
export function ManifestDocumentActions({ manifest, tenantId }: ManifestDocumentActionsProps) {
  const { canQueueManifestExtraction } = useQueueManifestDocumentExtraction();
  const hasDocumentAI = useDocumentAI();
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  if (!canQueueManifestExtraction) return null;

  const hasSourceDocument = !!manifest.sourceDocument;
  const hasExtractedData = !!manifest.documentManifestId;
  const needsReview = !!manifest.sourceDocument?.documentMetadata?.needsReview;

  return (
    <>
      {needsReview && (
        <Chip
          icon={<SparkleIcon sx={{ fontSize: 16 }} />}
          label="Needs Review"
          size="small"
          color="warning"
          variant="outlined"
        />
      )}

      {hasSourceDocument && (
        <Tooltip title={hasExtractedData ? "Review extraction data" : "View document"}>
          <IconButton size="small" onClick={() => setShowReviewDialog(true)}>
            <DescriptionOutlined />
          </IconButton>
        </Tooltip>
      )}

      {!hasSourceDocument && (
        <Tooltip title="Upload manifest document">
          <IconButton size="small" onClick={() => setShowUploadDialog(true)}>
            <UploadFile />
          </IconButton>
        </Tooltip>
      )}

      {showReviewDialog && manifest.sourceDocument && (
        <ReviewDocumentMetadataDialog
          open
          onClose={() => setShowReviewDialog(false)}
          documentIds={[manifest.sourceDocument.id]}
          tenantId={tenantId}
          hasDocumentAI={hasDocumentAI}
          wasteManifestId={
            hasExtractedData ? (manifest.documentManifestId ?? undefined) : undefined
          }
        />
      )}

      {showUploadDialog && (
        <ManifestUploadDialog
          open
          onClose={() => setShowUploadDialog(false)}
          tenantId={tenantId}
          facilityId={manifest.facility?.id}
          manifestTrackingNumber={manifest.manifestTrackingNumber ?? undefined}
          generatorId={manifest.epaId ?? undefined}
        />
      )}
    </>
  );
}
